import { IconRalphWiggum } from "@/assets/icons/IconRalphWiggum";
import { Markdown } from "@/components/ai/chat/Markdown";
import { parseAgentModePrefix, type AgentModePrefix } from "@/components/flexkit/chat/agent-mode-prefix";
import { cn } from "@/components/podkit/lib/cn";
import type { UserMessage } from "@/hooks/use-stream-conversation";
import { IconClock } from "central-icons/IconClock";
import { IconSearchlinesSparkle } from "@central-icons-react/round-outlined-radius-2-stroke-1.5/IconSearchlinesSparkle";
import { IconCodeAssistant } from "@central-icons-react/round-outlined-radius-2-stroke-1.5/IconCodeAssistant";
import { useEffect, useState, type FC } from "react";

const modeConfig = {
    agent: {
        label: "Agent",
        Icon: IconCodeAssistant,
        colorClass: "text-content-brand",
    },
    spec: {
        label: "Spec",
        Icon: IconSearchlinesSparkle,
        colorClass: "text-content-brand-accent-01",
    },
    ralph: {
        label: "Ralph",
        Icon: IconRalphWiggum,
        colorClass: "text-content-orange",
    },
};

const AgentModeBadge: FC<{ mode: AgentModePrefix }> = ({ mode }) => {
    const { label, Icon, colorClass } = modeConfig[mode];

    return (
        <div className={cn("flex items-center gap-1 font-normal", colorClass)}>
            <Icon size={14} className="shrink-0" />
            <span className="text-xs">{label}</span>
        </div>
    );
};

const UserImage: FC<{ data: Uint8Array; mimeType: string }> = ({ data, mimeType }) => {
    const [imageUrl, setImageUrl] = useState<string>("");

    useEffect(() => {
        if (!data || data.length === 0) {
            return;
        }

        // Handle case where data might be a regular array or object instead of Uint8Array
        let blobData: BlobPart;
        if (data instanceof Uint8Array) {
            blobData = data;
        } else if (Array.isArray(data)) {
            blobData = new Uint8Array(data);
        } else if (typeof data === "object" && data !== null) {
            // Protobuf-es might return an object with numeric keys
            const values = Object.values(data as Record<string, number>);
            blobData = new Uint8Array(values);
        } else {
            return;
        }

        const blob = new Blob([blobData], { type: mimeType || "image/png" });
        const url = URL.createObjectURL(blob);
        setImageUrl(url);

        // Cleanup on unmount only, not on re-render
        return () => {
            URL.revokeObjectURL(url);
        };
    }, [data, mimeType]);

    if (!imageUrl) {
        return null;
    }

    return <img src={imageUrl} alt="User uploaded image" className="max-h-64 max-w-full rounded-lg object-contain" />;
};

export const UserMsg: FC<{
    message: UserMessage;
    isPending: boolean;
}> = ({ message, isPending }) => {
    // Check if any fragment has an agent mode prefix
    const agentMode = message.fragments.reduce<AgentModePrefix>((found, fragment) => {
        if (found !== "agent") return found;
        if (fragment.type === "text") {
            return parseAgentModePrefix(fragment.content).mode;
        }
        return "agent";
    }, "agent");

    return (
        // the class user-message is used within the markdown component to style some markdown elements differently when presented
        // inside a user message.
        <div
            className="user-message flex w-fit max-w-full flex-col items-end gap-1 self-end"
            data-message-id={`${message.type}-${message.id}`}
        >
            <AgentModeBadge mode={agentMode} />
            <div
                className={cn(
                    "min-w-0 max-w-full flex-1 rounded-xl border border-border-subtle bg-surface-muted px-3 py-2 text-base text-content-primary shadow-sm transition-opacity",
                    {
                        "opacity-50": isPending,
                    },
                )}
            >
                <div className="flex gap-2">
                    {isPending && <IconClock className="mt-[1px] shrink-0 text-content-primary" size="14" />}
                    <div className="flex min-w-0 flex-1 flex-col gap-2">
                        {message.fragments.map((fragment) => {
                            switch (fragment.type) {
                                case "text": {
                                    const { strippedContent } = parseAgentModePrefix(fragment.content);
                                    return <Markdown content={strippedContent} key={fragment.id} />;
                                }
                                case "image": {
                                    return (
                                        <UserImage
                                            key={fragment.id}
                                            data={fragment.data}
                                            mimeType={fragment.mimeType}
                                        />
                                    );
                                }
                            }
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};
